import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, lockedPath } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Library from "@/pages/Library";
import TutorialReader from "@/pages/TutorialReader";
import MyLearning from "@/pages/MyLearning";
import Reflections from "@/pages/Reflections";
import Companion from "@/pages/Companion";
import Account from "@/pages/Account";
import Clients from "@/pages/Clients";
import Billing from "@/pages/Billing";
import { PaymentSuccess, PaymentCancel, Locked } from "@/pages/PaymentPages";
import Login from "@/pages/auth/Login";
import Apply from "@/pages/auth/Apply";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import TutorialEditor from "@/pages/admin/TutorialEditor";
import CategoriesManager from "@/pages/admin/CategoriesManager";
import Applications from "@/pages/admin/Applications";
import UsersAdmin from "@/pages/admin/UsersAdmin";
import VideoSlots from "@/pages/admin/VideoSlots";

const Layout = () => (
  <div className="min-h-screen bg-background">
    <Navbar />
    <Outlet />
  </div>
);

const Home = () => {
  const { user, hasAccess } = useAuth();
  if (user === null) return null;
  if (!user) return <Landing />;
  if (!hasAccess) return <Navigate to={lockedPath(user)} replace />;
  return <Library />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/register" element={<Navigate to="/apply" replace />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/set-password" element={<ResetPassword setup />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/account" element={<Account />} />
              <Route path="/locked" element={<Locked />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
            </Route>
            <Route element={<ProtectedRoute requireAccess />}>
              <Route path="/tutorials/:id" element={<TutorialReader />} />
              <Route path="/my-learning" element={<MyLearning />} />
              <Route path="/reflections" element={<Reflections />} />
              <Route path="/companion" element={<Companion />} />
            </Route>
            <Route element={<ProtectedRoute therapistOnly />}>
              <Route path="/clients" element={<Clients />} />
              <Route path="/billing" element={<Billing />} />
            </Route>
            <Route element={<ProtectedRoute adminOnly />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/categories" element={<CategoriesManager />} />
              <Route path="/admin/applications" element={<Applications />} />
              <Route path="/admin/users" element={<UsersAdmin />} />
              <Route path="/admin/videos" element={<VideoSlots />} />
              <Route path="/admin/tutorials/new" element={<TutorialEditor />} />
              <Route path="/admin/tutorials/:id" element={<TutorialEditor />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
